import React, { forwardRef, memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CancelIcon, CheckMarkIcon, FullScreenIcon, ImagineLogo, LeftIcon, RightIcon } from '../../assets/icons';
import { Download, FooterButton, Loader, ModalButtonArea } from '../../common';
import ImagePreviewSlider from '../../common/ImagePreviewSlider';
import MenuDropdown from '../../common/MenuDropdown';
import Button from '../../components/Button/Button';
import BinIcon from '../../components/icons/BinIcon';
import { DOWNLOAD_ICON } from '../../constants/assetConstants';
import { PRODUCT_DETAILS_PAGE_LABEL, PRODUCT_ROOM_TYPE_VALUE } from '../../constants/constants';
import { ROOM_TYPE } from '../../constants/roomConstants';
import { useUserContext } from '../../contexts/UserContext';
import {
  apiDeleteMultipleProductImages,
  apiDeleteProductImages,
  apiGetProductGifsById,
  apiGetProductGifsByIdPublic,
  apiGetProductRendersById,
  apiGetProductRendersByIdPublic,
  apiUpdateProductImageOrder,
  apiUpdateProductImageName,
  apiDeleteProductGifs,
  apiUpdateProductGifs,
} from '../../helpers/api';
import { getFormData, handleDownloadImage, handleToggleScreen } from '../../helpers/jsHelper';
import { successToast, warningToast } from '../../helpers/toastHelper';
import { useInfoDialogaActions } from '../../hooks/useInfoDialogaActions';
import { ReactSortable } from 'react-sortablejs';
import { fetchAllPages } from '../../helpers/apiHelper';
import ThumbnailCardUI from '../../common/ThumbnailCardUI';
import { PASS } from '../../constants/renderPreviewConstants';

const ReOrderImageModal = (props) => {
  const [loading, setLoading] = useState(false);
  const { show, onClose, isEditor, productId, ...rest } = props || {};
  const [list, setList] = useState([]);

  useEffect(async () => {
    if (!show) return;
    setLoading(true);
    const res = await fetchAllPages((page) => {
      const payload = {
        product: productId,
        room_type: props?.imageType,
        page: page,
        per_page: 20,
      };
      return apiGetProductRendersById(payload);
    });
    setLoading(false);
    if (res && res?.results?.length > 0) {
      const list = res.results.map((a) => ({
        id: a?.id,
        name: a?.name,
        angleName: a?.render_image_details?.angle_name,
        thumbnail: a?.thumbnail,
        sequence_number: +a?.sequence_number,
      }));
      setList(list.sort((a, b) => a?.sequence_number - b?.sequence_number));
    }
  }, [show]);

  const handleUpdateOrderAPI = async (payload) => {
    await apiUpdateProductImageOrder(payload);
  };

  const handleUpdateOrder = (value) => {
    if (isEditor) {
      if (value?.newIndex || value?.newIndex === 0) {
        const formData = new FormData();
        list.map((a, index) => {
          formData.append('id', a?.id);
          formData.append('sequence_number', index + 1);
        });
        handleUpdateOrderAPI(formData);
      }
    }
  };

  return (
    <>
      <ModalButtonArea
        {...rest}
        size="xl"
        startOpen={show}
        centered={true}
        bigButton={false}
        bigIcon={true}
        variant="link"
        buttonTitle={rest.buttonTitle}
        iconBefore={rest.iconBefore}
        onClose={onClose}
        checkForPermission="UPLOAD_TEXTURE"
        className="upload-texture-modal lifystyle-popup"
        disabled={rest.disableButton}
        backdropClassName="overlay-dark"
        headingLeft
        title={
          <>
            <h4>
              {props?.title}{' '}
              <small style={{ fontSize: '1rem', fontWeight: 400 }}>
                (Drag and drop to re-order the images)
              </small>
            </h4>
          </>
        }
        body={
          <div style={{ minHeight: 500 }}>
            {loading ? <Loader loading position="fixed" /> : null}
            {list.length === 0 && !loading && (
              <div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                Currently don&rsquo;t have any {props?.title?.toLowerCase()}.
              </div>
            )}
            <ReactSortable
              expand={true}
              tag={CustomComponent}
              list={list}
              onSort={handleUpdateOrder}
              setList={isEditor ? setList : () => {}}>
              {list.map((item) => {
                const { id, thumbnail, name, angleName, image_file, gif_file } = item;
                return (
                  <ThumbnailCardUI
                    key={id}
                    id={id}
                    thumbnail={thumbnail || image_file || gif_file}
                    className="smallThumbCard"
                    name={name}
                    secondLine={angleName}
                    imageProps={{
                      contain: false,
                      rounded: false,
                    }}
                  />
                );
              })}
            </ReactSortable>
          </div>
        }
      />
    </>
  );
};

const CustomComponent = forwardRef((props, ref) => {
  return (
    <div className="model-grid" ref={ref}>
      {props.children}
    </div>
  );
});

export const SilosListShowCase = memo(
  ({
    title = 'Imagery',
    imageType = ROOM_TYPE.SILO,
    setInitialTab,
    setType,
    setShowModal,
    setImageListForOpenModalType,
    productId,
    refresh,
    setRefresh,
    isPublicPage,
    filteredSeparateActionMenu,
    handleOpenCurator,
    getImageSrc,
    getImageTypeFooterComponent,
    actionMenu,
  }) => {
    const dispatch = useDispatch();
    const { canUpdatePdpV1Content, currentUser, isGuestUser } = useUserContext();
    const { openInfoDialog } = useInfoDialogaActions();
    const [isOpen, setIsOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [imageList, setImageList] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pass, setPass] = useState(PASS.STANDARD);
    const [browserWidth, setBrowserWidth] = useState(0);
    const [renameValue, setRenameValue] = useState(null);
    const [showReOrder, setShowReOrder] = useState(false);

    const setWindowDimensions = () => {
      setBrowserWidth(document?.documentElement?.clientWidth);
    };

    useEffect(() => {
      setWindowDimensions();
      window.addEventListener('resize', setWindowDimensions);
      return () => {
        window.removeEventListener('resize', setWindowDimensions);
      };
    }, []);

    useEffect(() => {
      fetchRenderData();
    }, [page]);

    useEffect(() => {
      if (refresh && refresh === PRODUCT_ROOM_TYPE_VALUE.SILO) {
        if (page === 1) {
          fetchRenderData();
        } else {
          setPage(1);
        }
        setRefresh(null);
      }
    }, [refresh]);

    const fetchRenderData = async () => {
      const key = isPublicPage ? 'unicode' : 'product';
      // const payload = {
      //   [key]: productId,
      //   room_type: imageType,
      //   page: page,
      //   per_page: 20,
      // };
      setLoading(true);
      const apiGif = isPublicPage ? apiGetProductGifsByIdPublic : apiGetProductGifsById;
      const api = isPublicPage ? apiGetProductRendersByIdPublic : apiGetProductRendersById;

      const result = await fetchAllPages((page) => {
        return api({
          [key]: productId,
          room_type: imageType, // ROOM_TYPE?.LIFESTYLE,
          page: page,
          per_page: 20,
        });
      });

      const resultGif = await fetchAllPages((page) => {
        return apiGif({
          [key]: productId,
          room_type: imageType, // ROOM_TYPE?.LIFESTYLE,
          page: page,
          per_page: 20,
        });
      });

      setLoading(false);
      setHasMore(result?.next_link ? true : false);
      let _pass = {};
      result?.results?.forEach(
        (_item) =>
          (_pass[_item.id] = {
            default: _item.render_image_details?.img_type,
            current: _item.render_image_details?.img_type,
          })
      );

      if (page > 1) {
        setImageList([...imageList, ...result?.results]);
        setPass({ ...pass, ..._pass });
      } else {
        setImageList([...resultGif?.results, ...result?.results]);
        setPass(_pass);
      }
    };

    const filterMenu = filteredSeparateActionMenu(currentUser, isPublicPage).filter(
      ({ permissionsRequired }) => !permissionsRequired || permissionsRequired === canUpdatePdpV1Content
    );

    const handleSelect = async (value, data) => {
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.RENAME_VALUE) {
        setRenameValue(data);
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.EDIT_IN_3D_VALUE) {
        handleOpenCurator(data, dispatch);
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.DELETE_ALL_VALUE) {
        if (imageList?.length) {
          openInfoDialog({
            title: `Are you sure you want to delete all ${title?.toLowerCase()}?`,
            description: '',
            cancelButtonText: 'No',
            buttonText: 'Yes',
            onButtonClick: async () => {
              // Separate regular images
              const imageIds = [];

              // Create promises array
              const deletePromises = [];

              imageList.forEach((image) => {
                if (image?.gif_file) {
                  deletePromises.push(apiDeleteProductGifs(image.id));
                } else {
                  imageIds.push(image.id);
                }
              });

              // Delete all images in a single call
              if (imageIds.length > 0) {
                const formData = getFormData(imageIds.map((id) => ({ product_room: id })));
                deletePromises.push(apiDeleteMultipleProductImages(formData));
              }

              const res = await Promise.allSettled(deletePromises);
              await fetchRenderData();
              if (res?.filter((a) => a.status === 'fulfilled')?.length > 0) {
                successToast('Items deleted successfully.');
                if (page > 1) {
                  setPage(1);
                  setHasMore(false);
                }
                //  else {
                //   fetchRenderData();
                // }
              }
            },
            okBtnColor: data?.okBtnColor,
            cancelBtnColor: data?.cancelBtnColor,
            okBtnClassName: data?.okBtnClassName,
          });
        }
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.UPLOAD_VALUE) {
        setType(PRODUCT_ROOM_TYPE_VALUE.SILO);
        setImageListForOpenModalType(imageList);
        setShowModal(true);
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.DELETE_VALUE) {
        openInfoDialog({
          title: `Are you sure you want to delete this ${data?.name}?`,
          description: '',
          cancelButtonText: 'No',
          buttonText: 'Yes',
          onButtonClick: async () => {
            const api = data?.gif_file ? apiDeleteProductGifs : apiDeleteProductImages;
            const res = await api(data?.id);
            if (res) {
              if (window?.document?.fullscreen || window.fullscreen) {
                handleToggleScreen();
              }
              successToast('Item deleted successfully.');
              setIsOpen(false);
              setPhotoIndex(0);
              setImageList(imageList.filter((_images) => _images.id !== data.id));
            }
          },
          okBtnColor: data?.okBtnColor,
          cancelBtnColor: data?.cancelBtnColor,
          okBtnClassName: data?.okBtnClassName,
        });
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.RE_ORDER_VALUE) {
        setShowReOrder(true);
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.UPLOAD_GIF_VALUE) {
        setType(PRODUCT_ROOM_TYPE_VALUE.SILO);
        setInitialTab(PRODUCT_ROOM_TYPE_VALUE.UPLOAD_GIF);
        setImageListForOpenModalType(imageList);
        setShowModal(true);
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.UPLOAD_IMAGE_VALUE) {
        setType(PRODUCT_ROOM_TYPE_VALUE.SILO);
        setInitialTab(PRODUCT_ROOM_TYPE_VALUE.UPLOAD_IMAGES);
        setImageListForOpenModalType(imageList);
        setShowModal(true);
      }
    };

    const footerActions = [
      {
        element: Download,
        title: 'Download',
        footerButton: true,
        iconSrc: DOWNLOAD_ICON,
        clickFunction: () =>
          handleDownloadImage({
            url: getImageSrc(imageList?.[photoIndex], pass),
            name: imageList?.[photoIndex]?.name,
          }),
        seq: 1,
        permissionsRequired: false,
      },
      {
        element: FooterButton,
        title: 'Edit in 3D',
        footerButton: currentUser,
        icon: ImagineLogo,
        clickFunction: () =>
          handleSelect(
            { value: PRODUCT_DETAILS_PAGE_LABEL.EDIT_IN_3D_VALUE },
            imageList?.[photoIndex]
          ),
        seq: 2,
        hideInCaseOf: isGuestUser(),
        permissionsRequired: false,
      },
      {
        element: FooterButton,
        title: 'Full screen',
        footerButton: true,
        icon: FullScreenIcon,
        clickFunction: () => {
          handleToggleScreen();
        },
        seq: 3,
        permissionsRequired: false,
      },
      {
        element: FooterButton,
        title: 'Delete',
        footerButton: currentUser,
        icon: BinIcon,
        clickFunction: () =>
          handleSelect({ value: PRODUCT_DETAILS_PAGE_LABEL.DELETE_VALUE }, imageList?.[photoIndex]),
        seq: 4,
        hideInCaseOf: isGuestUser(),
        permissionsRequired: true,
      },
    ];

    const handleFooter = () => {
      const _footerActions = imageList?.[photoIndex]?.render_image_details
        ? footerActions
        : footerActions.filter((a) => !a.title?.toLowerCase()?.includes('edit in 3d'));
      return (
        <>
          {getImageTypeFooterComponent(pass, setPass, imageList, photoIndex)}
          <footer className={'footer-style preview-display-footer'}>
            <h1 className="title px-0">{imageList?.[photoIndex]?.name || ''}</h1>
            {_footerActions.length > 0 && (
              <ul className="footer-action-list ced-action">
                {_footerActions
                  ?.filter((a) => !a?.hideInCaseOf)
                  ?.filter(
                    ({ permissionsRequired }) =>
                      !permissionsRequired || permissionsRequired === canUpdatePdpV1Content
                  )
                  ?.map((a, index) => {
                    const { element, ...rest } = a;
                    const Component = element;
                    return (
                      <Component
                        // shareId={id}
                        id={`action${index}}`}
                        key={`action${index}}`}
                        {...rest}
                      />
                    );
                  })}
              </ul>
            )}
          </footer>
        </>
      );
    };

    if (!canUpdatePdpV1Content && !imageList?.length) {
      return null;
    }

    const OnSave = async (name) => {
      if (loading) {
        return;
      }
      if (name !== renameValue?.name) {
        if (!renameValue?.name) {
          warningToast('Name is required.');
          return;
        }
        const formData = getFormData([{ name: renameValue?.name }]);
        setLoading(true);
        const api = renameValue?.gif_file ? apiUpdateProductGifs : apiUpdateProductImageName;
        const res = await api(renameValue?.id, formData);
        setLoading(false);
        if (res?.status && res?.data) {
          const indexVal = imageList?.findIndex(({ id }) => +id === renameValue?.id);
          if (~indexVal) {
            const cloneList = imageList?.slice();
            cloneList[indexVal]['name'] = res?.data?.name;
            setImageList(cloneList);
            setRenameValue(null);
          } else {
            setRenameValue(null);
          }
        }
      } else {
        setRenameValue(null);
      }
    };

    return (
      <>
        {showReOrder && canUpdatePdpV1Content && !isPublicPage ? (
          <ReOrderImageModal
            title={title}
            show={showReOrder}
            imageType={imageType}
            onClose={() => {
              setPage(1);
              fetchRenderData();
              setShowReOrder(false);
            }}
            productId={productId}
            isEditor={canUpdatePdpV1Content}
          />
        ) : null}
        {isOpen ? (
          <ImagePreviewSlider
            title={imageList?.[photoIndex]?.name || ''}
            imagesList={(imageList?.length > 0 && imageList?.map((list) => list)) || []}
            {...{ setIsOpen, photoIndex, setPhotoIndex, handleToggleScreen, pass, setPass }}
            footer={handleFooter()}
          />
        ) : null}

        <div className="life-style-header">
          <h6 className="main-title">{title}</h6>
          <div className="d-flex align-items-center">
            <button
              className={`arrow-left arrow ${
                imageList?.length > 2 && browserWidth < 1189
                  ? ''
                  : imageList?.length < 4
                  ? 'hide-button'
                  : ''
              }`}>
              <LeftIcon />
            </button>
            <button
              className={`arrow-right arrow ${
                imageList?.length > 2 && browserWidth < 1189
                  ? ''
                  : imageList?.length < 4
                  ? 'hide-button'
                  : ''
              }`}>
              <RightIcon />
            </button>
            {!isPublicPage && canUpdatePdpV1Content && !isGuestUser() && (
              <MenuDropdown
                defaultPrevent
                btnClassName="py-0 pe-0"
                menu={actionMenu}
                handleSelect={handleSelect}
              />
            )}
          </div>
        </div>
        {imageList?.length === 0 && (
          <div className="empty-area mb-3">
            {!loading && <span>{`Currently don't have any ${title?.toLowerCase()}.`}</span>}
          </div>
        )}
        {imageList?.length > 0 && (
          <div className={`grid-section grid-carousel mb-4 ${isPublicPage ? 'public' : ''}`}>
            <Swiper
              spaceBetween={12}
              onSlideChange={(e) => {
                hasMore && e?.isEnd && setPage(page + 1);
              }}
              a11y={false}
              onSwiper={(swiper) => {}}
              navigation={{ nextEl: '.arrow-right', prevEl: '.arrow-left' }}
              breakpoints={{
                0: {
                  slidesPerView: 1.5,
                },
                320: {
                  slidesPerView: 1.5,
                },
                768: {
                  slidesPerView: 2,
                },
                1199: {
                  slidesPerView: 3,
                },
                1921: {
                  slidesPerView: 4,
                },
                2150: {
                  slidesPerView: 5,
                },
              }}>
              {imageList?.length > 0 &&
                imageList?.map((image, index) => {
                  return (
                    <SwiperSlide key={index}>
                      <div className="life-style-body mb-0">
                        {image?.gif_file ? <div className="gif-type-placeholder">GIF</div> : null}
                        <img
                          src={image?.thumbnail || image?.image_file || image?.gif_file}
                          role="presentation"
                          onClick={() => {
                            setIsOpen(true);
                            setPhotoIndex(index);
                          }}
                        />
                        <div className="life-style-title">
                          {renameValue && +renameValue?.id === +image?.id ? (
                            <div className="name-input-container">
                              <input
                                className={`form-control input-bg-color`}
                                value={renameValue?.name}
                                type="text"
                                onChange={(e) =>
                                  setRenameValue({
                                    ...renameValue,
                                    name: e.target?.value,
                                  })
                                }
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter') return;
                                  OnSave(image?.name);
                                }}
                              />

                              <Button
                                color="light"
                                onClick={() => setRenameValue(null)}
                                size="xlg"
                                disabled={loading}
                                className="so-icon-button"
                                iconBefore={<CancelIcon />}
                              />
                              <Button
                                disabled={loading}
                                color="dark"
                                onClick={() => OnSave(image?.name)}
                                size="xlg"
                                className="so-icon-button"
                                loading={loading}
                                iconBefore={<CheckMarkIcon />}
                              />
                            </div>
                          ) : (
                            <>
                              <div>
                                <h6>{image?.name || ''}</h6>
                                {image?.description && <p>{image?.description}</p>}
                              </div>
                              {filterMenu?.length > 0 && !isGuestUser() && (
                                <MenuDropdown
                                  defaultPrevent
                                  menu={
                                    image?.render_image_details
                                      ? filterMenu
                                      : filterMenu.filter(
                                          (a) =>
                                            a.value !== PRODUCT_DETAILS_PAGE_LABEL.EDIT_IN_3D_VALUE
                                        )
                                  }
                                  url={image?.image_file || image?.gif_file}
                                  list={image}
                                  handleSelect={handleSelect}
                                  btnClassName="p-0"
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
            </Swiper>
          </div>
        )}
      </>
    );
  }
);
